import { motion } from "framer-motion";
import { featureFilm } from "../../data/portfolio";

// Minimalist status card used in the horizontal row of three.
const PitchCard = ({ label, items, delay = 0 }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-120px" }}
    transition={{ duration: 1.1, delay, ease: [0.19, 1, 0.22, 1] }}
    className="group relative flex-1 min-h-[220px] sm:min-h-[260px] border p-8 sm:p-10 flex flex-col bg-black/40 backdrop-blur-[2px] transition-colors duration-500 hover:border-[color:var(--accent-amber)]"
    style={{ borderColor: "rgba(200,162,91,0.28)" }}
  >
    <span
      className="text-[10px] uppercase tracking-[0.4em] mb-8"
      style={{ color: "var(--accent-amber)" }}
    >
      {label}
    </span>
    <ul className="mt-auto space-y-3">
      {items.map((it, i) => (
        <li
          key={i}
          className="font-serif text-lg sm:text-xl leading-tight text-white/90"
        >
          {it}
        </li>
      ))}
    </ul>
  </motion.article>
);

export const FeatureFilm = () => {
  return (
    <section
      id="feature"
      data-testid="feature-section"
      className="relative border-t border-white/10 overflow-hidden bg-[#050505]"
    >
      {/* Palette derived from the poster: snowy blue, warm cabin amber, ember red */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src={featureFilm.poster}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-[0.09]"
          style={{ filter: "blur(40px) saturate(1.2)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 20% 15%, rgba(178,200,224,0.10), transparent 55%), radial-gradient(ellipse at 82% 78%, rgba(215,150,80,0.11), transparent 55%), radial-gradient(ellipse at 50% 100%, rgba(178,58,58,0.06), transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,5,0.4) 0%, transparent 25%, transparent 75%, rgba(5,5,5,0.6) 100%)",
          }}
        />
        <div className="absolute inset-0 grain opacity-30" />
      </div>

      {/* ============ HERO — poster anchors, title & meta on the right ============ */}
      <div className="relative z-10 px-6 sm:px-10 lg:px-16 pt-32 sm:pt-40 lg:pt-48 pb-24 sm:pb-32">
        <div className="max-w-[1600px] mx-auto">
          {/* Overline + status pill */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="flex items-center gap-6 mb-16 sm:mb-24"
          >
            <span className="text-[10px] uppercase tracking-[0.4em] text-white/40">
              <span style={{ color: "var(--accent-amber)" }}>03</span> — Feature Film
            </span>
            <span className="h-px flex-1 max-w-[120px] bg-white/10" />
            <span
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em]"
              style={{ color: "var(--accent-amber)" }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ backgroundColor: "var(--accent-amber)" }}
              />
              In Development
            </span>
          </motion.div>

          {/* Heading block — title + meta stacked, left aligned */}
          <div className="mb-12 sm:mb-14 lg:mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 1.2, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
              className="font-serif text-[96px] sm:text-[120px] lg:text-[140px] font-light leading-[0.9] tracking-tighter text-white"
              data-testid="feature-title"
            >
              {featureFilm.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-6 sm:mt-7 flex items-center gap-4 text-[11px] uppercase tracking-[0.42em]"
            >
              <span style={{ color: "var(--accent-amber)" }}>
                {featureFilm.duration}
              </span>
              <span
                className="inline-block w-1 h-1 rounded-full"
                style={{ backgroundColor: "#c94a4a" }}
              />
              <span className="text-white/85">{featureFilm.genre}</span>
            </motion.p>
          </div>

          <div className="grid grid-cols-12 gap-8 sm:gap-12 lg:gap-16 items-start">
            {/* Left column — poster with creator credits directly beneath */}
            <div className="col-span-12 lg:col-span-7">
              <motion.figure
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{ duration: 1.4, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
              >
                <div className="relative">
                  {/* Warm cabin glow spilling from the poster */}
                  <div
                    className="absolute -inset-8 sm:-inset-14 -z-10 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse at 40% 70%, rgba(220,150,70,0.18), transparent 55%), radial-gradient(ellipse at 30% 20%, rgba(178,205,230,0.12), transparent 55%)",
                    }}
                  />
                  <a
                    href="#feature-cta"
                    data-testid="feature-poster-link"
                    aria-label={`Learn more about ${featureFilm.title}`}
                    className="group relative block w-full overflow-hidden cursor-pointer shadow-[0_60px_160px_rgba(0,0,0,0.8)] transition-shadow duration-700 hover:shadow-[0_70px_180px_rgba(200,162,91,0.28)]"
                  >
                    <img
                      src={featureFilm.poster}
                      alt={`${featureFilm.title} — official feature film poster by Valeriy Rudenko`}
                      data-testid="feature-poster"
                      loading="lazy"
                      decoding="async"
                      className="block w-full h-auto transition-transform duration-[1400ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.025]"
                    />
                    {/* Subtle amber wash on hover */}
                    <div
                      aria-hidden
                      className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700 mix-blend-soft-light"
                      style={{
                        background:
                          "linear-gradient(180deg, transparent 55%, rgba(200,162,91,0.18) 100%)",
                      }}
                    />
                    {/* "Learn more →" hover chip */}
                    <span
                      aria-hidden
                      className="absolute bottom-5 right-5 sm:bottom-6 sm:right-6 inline-flex items-center gap-2 px-3.5 py-2 text-[10px] uppercase tracking-[0.35em] text-white border border-white/45 backdrop-blur-sm bg-black/35 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-[cubic-bezier(0.19,1,0.22,1)]"
                    >
                      Learn More
                      <span className="transition-transform duration-500 group-hover:translate-x-0.5">→</span>
                    </span>
                  </a>
                </div>
              </motion.figure>

              {/* Creator credits — directly beneath the poster */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{ duration: 1, delay: 0.35 }}
                className="mt-10 sm:mt-12 pt-7 border-t"
                style={{ borderColor: "rgba(178,200,220,0.14)" }}
              >
                <p className="text-[10px] uppercase tracking-[0.4em] text-white/40 mb-3">
                  Creator
                </p>
                <p className="font-serif text-2xl sm:text-3xl font-light text-white leading-tight">
                  {featureFilm.creator.name}
                </p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-white/55">
                  {featureFilm.creator.role}
                </p>
              </motion.div>
            </div>

            {/* Right column — logline, top-aligned with the poster's top edge */}
            <div className="col-span-12 lg:col-span-5 lg:pl-4 xl:pl-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{ duration: 1, delay: 0.4 }}
              >
                <p
                  className="text-[10px] uppercase tracking-[0.4em] mb-5"
                  style={{ color: "var(--accent-amber)" }}
                >
                  Logline
                </p>
                <p className="font-serif text-[19px] sm:text-[21px] lg:text-[22px] leading-[1.7] text-white/85 max-w-none">
                  {featureFilm.logline}
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ THREE PITCH CARDS ============ */}
      <div className="relative z-10 px-6 sm:px-10 lg:px-16 pb-32 sm:pb-40">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
            <PitchCard
              label="Status"
              items={["In Development", "Screenplay in Progress"]}
              delay={0}
            />
            <PitchCard
              label="Seeking"
              items={[
                "Producing Partner",
                "Co-production Partner",
                "Financing Partner",
                "Sales Agent",
              ]}
              delay={0.1}
            />
            <PitchCard
              label="Format"
              items={["Historical Thriller", "120 min", "Feature Film"]}
              delay={0.2}
            />
          </div>
        </div>
      </div>

      {/* ============ FINAL MINIMAL CTA ============ */}
      <div id="feature-cta" className="relative z-10 px-6 sm:px-10 lg:px-16 pb-40 sm:pb-56 lg:pb-64">
        <div className="max-w-[900px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="h-px w-16 mx-auto mb-14 sm:mb-16"
            style={{ backgroundColor: "var(--accent-amber)" }}
          />

          <motion.h3
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1.1, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif text-3xl sm:text-4xl lg:text-[46px] font-light leading-[1.15] tracking-tight text-white"
            data-testid="feature-cta-heading"
          >
            Interested in bringing
            <br />
            <span
              className="font-serif italic tracking-tighter"
              style={{ fontSize: "1.35em", lineHeight: "1", display: "inline-block", marginTop: "0.15em" }}
            >
              {featureFilm.title}
            </span>
            <br />
            to the screen?
          </motion.h3>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mt-10 sm:mt-12 text-sm sm:text-base leading-[1.7] text-white/60 font-sans max-w-md mx-auto"
          >
            Currently seeking producing partners,
            <br className="hidden sm:block" /> co-producers and international financing.
          </motion.p>

          <motion.a
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1, delay: 0.5, ease: [0.19, 1, 0.22, 1] }}
            href="#contact"
            data-testid="feature-cta-request-package"
            className="group mt-12 sm:mt-14 inline-flex items-center gap-4 border px-8 py-4 text-[11px] uppercase tracking-[0.35em] text-white transition-all duration-500 hover:text-black hover:-translate-y-0.5"
            style={{ borderColor: "var(--accent-amber)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--accent-amber)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            Request Film Package
            <span className="transition-transform duration-500 group-hover:translate-x-1">
              →
            </span>
          </motion.a>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1, delay: 0.7, ease: [0.19, 1, 0.22, 1] }}
            className="h-px w-16 mx-auto mt-16 sm:mt-20"
            style={{ backgroundColor: "rgba(200,162,91,0.4)" }}
          />
        </div>
      </div>
    </section>
  );
};
